/**
 * Adding a new notification:
 *
 * If the notification is a "basic notification" where you only need to
 * specify the type and text, you can add it to the basicNotificationMap.
 *
 * If it requires more complex content like a <Link> with some onClick behaviour,
 * you create the entire template and add it to the customNotificationMap.
 */
import type React from 'react';
import { connect } from 'react-redux';
import Notification from '@zg-rentals/multibrand-ui/lib/Notification';
import { Banner, Box, CloseButton } from '@zillow/constellation-10';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import ireneNotifications from '@rental-manager/notifications';
import renterNotifications from '@renter-hub/notifications';
import { stackedCloseNotification } from '@common/actions/NotificationActions';
import userPersonas from '@common/constants/userPersonas';
import './index.scss';
import cx from 'classnames';
import { logError } from '@zg-rentals/log-error';

const notificationMap = {
  landlord: ireneNotifications,
  renter: renterNotifications,
};

interface INotificationContainerComponentProps {
  persona: string;
  notifications?: any;
  dispatch: (...args: Array<any>) => any;
  className: string;
  modal?: boolean;
}

const StackedNotificationsContainerComponent: React.FC<
  React.PropsWithChildren<INotificationContainerComponentProps>
> = ({ className, persona, modal = false, notifications, dispatch }) => {
  const forPageOrModal = modal ? 'modal' : 'page';
  const notificationBanner: any = [];
  for (const key in notifications[forPageOrModal]) {
    if (notifications[forPageOrModal].hasOwnProperty(key)) {
      const onClose = () => dispatch(stackedCloseNotification(key));
      const { show, name, extraArgs } = notifications[forPageOrModal][key];
      if (show) {
        if (notificationMap[persona][name]) {
          const { custom = false, component, type, text: getText } = notificationMap[persona][name];
          if (custom) {
            const CustomNotification = component;
            notificationBanner.push(
              <Box css={{ marginBottom: 'default' }}>
                <CustomNotification
                  className={cx('txtWrap', className)}
                  onClose={onClose}
                  dispatch={dispatch}
                  {...extraArgs}
                />
              </Box>,
            );
          } else {
            const text = getText({ ...extraArgs });
            if (persona === userPersonas.LANDLORD) {
              notificationBanner.push(
                <>
                  <Notification className={cx('txtWrap', className)} type={type} onClose={onClose}>
                    {text}
                  </Notification>
                  <SpacerRow />
                </>,
              );
            } else {
              notificationBanner.push(
                <Banner tone={type} closeButton={<CloseButton onImpact />} css={{ marginBottom: 'default' }}>
                  {text}
                </Banner>,
              );
            }
          }
        }

        // If notification doesn't exist, log to splunk and don't render anything.
        logError({
          error: new Error('Could not find notification.'),
          context: `notification: ${name}, persona: ${persona}`,
        });
      }
    }
  }
  return notificationBanner.length > 0 ? notificationBanner : null;
};

StackedNotificationsContainerComponent.defaultProps = {
  persona: userPersonas.LANDLORD,
  notifications: {
    page: {},
  },
  dispatch: () => {},
};

const mapStateToProps = (state, { persona }) => {
  if (state && state.payments && state.payments[persona]) {
    return {
      notifications: state.payments[persona].stackedNotifications,
    };
  }
  return {};
};

export { StackedNotificationsContainerComponent };
export default connect(mapStateToProps)(StackedNotificationsContainerComponent);
